/* eslint-disable array-callback-return */
/* eslint-disable consistent-return */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

import Toolbar from '../../components/Toolbar';
import LoadingAnimation from '../../components/Loading';
import { useToast } from '../../hooks/toast';
import api from '../../services/api';
import formatValue from '../../utils/formatValue';

import {
  Container,
  Form,
  Content,
  HeaderRow,
  Title,
  SummaryGrid,
  StatCard,
  StatLabel,
  StatValue,
  ChartsGrid,
  ChartCard,
  ChartTitle,
  ControlsRow,
  FileButton,
  FileName,
  PrimaryButton,
} from './styles';

interface Stock { code: string; name: string; quantity: number; unit_price: number; }
interface Fii { code: string; name: string; quantity: number; unit_price: number; }
interface Tesouro { code: string; name: string; quantity: number; amount_invested: number; current_value: number; expiration_date: string; }
interface Portfolio { stocks: Stock[]; fiis: Fii[]; tesouro: Tesouro[]; }
interface TargetAsset { type: 'STOCK' | 'FII' | 'TD'; name: string; code: string; weight: number; }

// Tuned palette for dark theme readability (muted yellows)
const palette = ['#1DB954', '#C9A227', '#7086f5', '#E57373', '#64B5F6', '#81C784', '#B38F2A'];

const PortfolioDistributionV2: React.FC = () => {
  const [stocks, setStocks] = useState<Stock[]>([]);
  const [fiis, setFiis] = useState<Fii[]>([]);
  const [tesouro, setTesouro] = useState<Tesouro[]>([]);
  const [loading, setLoading] = useState(true);
  // Target distribution
  const [tStocks, setTStocks] = useState<TargetAsset[]>([]);
  const [tFiis, setTFiis] = useState<TargetAsset[]>([]);
  const [tTd, setTTd] = useState<TargetAsset[]>([]);
  const [fileBase64, setFileBase64] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const { addToast } = useToast();

  const containerRef = useRef<HTMLDivElement>(null);

  const reloadPositions = () => {
    return api.get<Portfolio>('wallet/positions').then((response) => {
      const portfolio = response.data;
      setStocks(portfolio.stocks || []);
      setFiis(portfolio.fiis || []);
      setTesouro(portfolio.tesouro || []);
    });
  };

  useEffect(() => {
    Promise.all([
      reloadPositions(),
      api.get<TargetAsset[]>('wallet/distribution').then((res) => {
        const dist = res.data || [];
        setTStocks(dist.filter((d) => d.type === 'STOCK'));
        setTFiis(dist.filter((d) => d.type === 'FII'));
        setTTd(dist.filter((d) => d.type === 'TD'));
      }),
    ]).finally(() => setLoading(false));
  }, []);

  const totals = useMemo(() => {
    const stocksCurrent = stocks.reduce((s, it) => s + (it.unit_price / 100) * it.quantity, 0);
    const fiisCurrent = fiis.reduce((s, it) => s + (it.unit_price / 100) * it.quantity, 0);
    const tesouroCurrent = tesouro.reduce((s, it) => s + it.current_value / 100, 0);

    const investedKnownStocks = 0; // not available from API
    const investedKnownFiis = 0; // not available from API
    const investedTesouro = tesouro.reduce((s, it) => s + it.amount_invested / 100, 0);

    const totalCurrent = stocksCurrent + fiisCurrent + tesouroCurrent;
    const investedDataAvailable = investedKnownStocks === 0 && investedKnownFiis === 0 && tesouro.length > 0;
    const totalInvested = investedDataAvailable ? investedTesouro : null; // only tesouro has invested data

    return { stocksCurrent, fiisCurrent, tesouroCurrent, totalCurrent, totalInvested };
  }, [stocks, fiis, tesouro]);

  const pieData = useMemo(() => (
    [
      { name: 'Ações', value: totals.stocksCurrent },
      { name: 'FIIs', value: totals.fiisCurrent },
      { name: 'Tesouro', value: totals.tesouroCurrent },
    ]
  ), [totals]);

  const stocksBarData = useMemo(() => stocks.map((s) => ({ name: s.code, value: (s.unit_price / 100) * s.quantity })), [stocks]);
  const fiisBarData = useMemo(() => fiis.map((f) => ({ name: f.code, value: (f.unit_price / 100) * f.quantity })), [fiis]);
  // Group Tesouro by code (group) to match grouping behavior
  const tesouroGrouped = useMemo(() => {
    const map: Record<string, number> = {};
    tesouro.forEach((t) => { map[t.code] = (map[t.code] || 0) + t.current_value / 100; });
    return Object.entries(map).map(([name, value]) => ({ name, value }));
  }, [tesouro]);

  // Combined distribution by individual asset (or TD group) using current value
  const combinedPercent = useMemo(() => {
    const items: { label: string; value: number; cls: 'stock' | 'fii' | 'td' }[] = [
      ...stocksBarData.map((d) => ({ label: d.name, value: d.value, cls: 'stock' as const })),
      ...fiisBarData.map((d) => ({ label: d.name, value: d.value, cls: 'fii' as const })),
      ...tesouroGrouped.map((d) => ({ label: d.name, value: d.value, cls: 'td' as const })),
    ];
    const total = totals.totalCurrent || 1;
    return items
      .map((it) => ({ ...it, percent: (it.value / total) * 100 }))
      .sort((a, b) => b.value - a.value);
  }, [stocksBarData, fiisBarData, tesouroGrouped, totals.totalCurrent]);

  const combinedMax = useMemo(() => {
    return combinedPercent.reduce((m, it) => Math.max(m, it.percent || 0), 0);
  }, [combinedPercent]);

  const combinedPercentTarget = useMemo(() => {
    const items: { label: string; percent: number; cls: 'stock' | 'fii' | 'td' }[] = [
      ...tStocks.map((d) => ({ label: d.code, percent: d.weight / 100, cls: 'stock' as const })),
      ...tFiis.map((d) => ({ label: d.code, percent: d.weight / 100, cls: 'fii' as const })),
      ...tTd.map((d) => ({ label: d.code, percent: d.weight / 100, cls: 'td' as const })),
    ];
    // Already in percentage units (sum ~ 100)
    return items.sort((a, b) => b.percent - a.percent);
  }, [tStocks, tFiis, tTd]);

  const combinedTargetMax = useMemo(() => {
    return combinedPercentTarget.reduce((m, it) => Math.max(m, it.percent || 0), 0);
  }, [combinedPercentTarget]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64String = reader.result as string;
        setFileBase64(base64String);
        setFileName(file.name);
      };
      reader.onerror = () => {
        addToast({ type: 'error', title: 'Erro', description: 'Falha ao ler arquivo' });
      };
    }
  };

  const handleUpload = async () => {
    if (!fileBase64) {
      addToast({ type: 'error', title: 'Erro', description: 'Selecione um arquivo primeiro' });
      return;
    }
    setUploading(true);
    try {
      await api.post('wallet/ingestion', { file: fileBase64.split(',')[1] });
      addToast({ type: 'success', title: 'Sucesso', description: 'Dados importados com sucesso!' });
      setFileBase64(null);
      setFileName(null);
      await reloadPositions();
    } catch (e) {
      addToast({ type: 'error', title: 'Erro na importação', description: 'Verifique o arquivo e tente novamente' });
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <Toolbar />
        <Form onSubmit={() => {}}>{/* spacer */}</Form>
        <div style={{ minHeight: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <LoadingAnimation />
        </div>
      </Container>
    );
  }

  // Render readable percentage labels outside the donut for contrast
  const renderPercentageLabel = (props: any) => {
    const RAD = Math.PI / 180;
    const {
      cx,
      cy,
      midAngle,
      outerRadius,
      percent,
    } = props;
    const r = outerRadius + 14; // place label slightly outside the arc
    const x = cx + r * Math.cos(-midAngle * RAD);
    const y = cy + r * Math.sin(-midAngle * RAD);
    const text = `${(percent * 100).toFixed(1)}%`;
    return (
      <text x={x} y={y} fill="#EAEAEA" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" style={{ fontWeight: 700 }}>
        {text}
      </text>
    );
  };

  return (
    <Container ref={containerRef}>
      <Toolbar />
      <Form onSubmit={() => {}}>{/* spacer */}</Form>
      <Content>
        <HeaderRow>
          <Title>Distribuição da carteira</Title>
        </HeaderRow>

        <ControlsRow>
          <FileButton htmlFor="fileInput">Selecionar planilha</FileButton>
          <input id="fileInput" type="file" accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel" style={{ display: 'none' }} onChange={handleFileChange} />
          {fileName && <FileName>{fileName}</FileName>}
          <PrimaryButton variant="primary" onClick={handleUpload} disabled={!fileBase64 || uploading}>
            {uploading ? 'Enviando...' : 'Enviar'}
          </PrimaryButton>
        </ControlsRow>

        <SummaryGrid>
          <StatCard>
            <StatLabel>Valor atual</StatLabel>
            <StatValue color="green">{formatValue(totals.totalCurrent)}</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>Total investido</StatLabel>
            <StatValue color={totals.totalInvested ? 'amber' : undefined}>
              {totals.totalInvested ? formatValue(totals.totalInvested) : 'N/D'}
            </StatValue>
          </StatCard>
        </SummaryGrid>

        <ChartsGrid>
          <ChartCard>
            <ChartTitle>Composição por classe (valor atual)</ChartTitle>
            <div style={{ width: '100%', height: 280 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    labelLine
                    label={renderPercentageLabel}
                  >
                    {pieData.map((_, i) => (
                      <Cell key={`slice-${i}`} fill={palette[i % palette.length]} />
                    ))}
                  </Pie>
                  <Legend verticalAlign="bottom" height={24} />
                  <Tooltip
                    formatter={(v: any) => formatValue(Number(v))}
                    contentStyle={{ background: '#1E1E1E', border: '1px solid rgba(255,255,255,0.1)', color: '#EAEAEA' }}
                    labelStyle={{ color: '#A0A0A0' }}
                    itemStyle={{ color: '#EAEAEA' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>

          <ChartCard>
            <ChartTitle>Ações (valor atual por ativo)</ChartTitle>
            <div style={{ width: '100%', height: 280 }}>
              <ResponsiveContainer>
                <BarChart data={stocksBarData} margin={{ left: 8, right: 8, bottom: 24 }}>
                  <XAxis
                    dataKey="name"
                    stroke="#A0A0A0"
                    interval={0}
                    angle={-35}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis stroke="#A0A0A0" tickFormatter={(v) => `${v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}`}/>
                  <Tooltip
                    formatter={(v: any) => formatValue(Number(v))}
                    contentStyle={{ background: '#1E1E1E', border: '1px solid rgba(255,255,255,0.1)', color: '#EAEAEA' }}
                    labelStyle={{ color: '#A0A0A0' }}
                    itemStyle={{ color: '#EAEAEA' }}
                  />
                  <Bar dataKey="value" fill={palette[0]} radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>

          <ChartCard>
            <ChartTitle>FIIs (valor atual por ativo)</ChartTitle>
            <div style={{ width: '100%', height: 280 }}>
              <ResponsiveContainer>
                <BarChart data={fiisBarData} margin={{ left: 8, right: 8, bottom: 24 }}>
                  <XAxis
                    dataKey="name"
                    stroke="#A0A0A0"
                    interval={0}
                    angle={-35}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis stroke="#A0A0A0" tickFormatter={(v) => `${v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}`}/>
                  <Tooltip
                    formatter={(v: any) => formatValue(Number(v))}
                    contentStyle={{ background: '#1E1E1E', border: '1px solid rgba(255,255,255,0.1)', color: '#EAEAEA' }}
                    labelStyle={{ color: '#A0A0A0' }}
                    itemStyle={{ color: '#EAEAEA' }}
                  />
                  <Bar dataKey="value" fill={palette[2]} radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>

          <ChartCard>
            <ChartTitle>Tesouro Direto (por grupo)</ChartTitle>
            <div style={{ width: '100%', height: 280 }}>
              <ResponsiveContainer>
                <BarChart data={tesouroGrouped} margin={{ left: 8, right: 8 }}>
                  <XAxis dataKey="name" stroke="#A0A0A0" />
                  <YAxis stroke="#A0A0A0" tickFormatter={(v) => `${v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}`}/>
                  <Tooltip
                    formatter={(v: any) => formatValue(Number(v))}
                    contentStyle={{ background: '#1E1E1E', border: '1px solid rgba(255,255,255,0.1)', color: '#EAEAEA' }}
                    labelStyle={{ color: '#A0A0A0' }}
                    itemStyle={{ color: '#EAEAEA' }}
                  />
                  <Bar dataKey="value" fill={palette[1]} radius={[6, 6, 0, 0]} fillOpacity={0.9} activeBar={{ fill: '#A98E20' }} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>
        </ChartsGrid>

        <ChartsGrid>
          <ChartCard>
            <ChartTitle>Percentual por ativo (valor atual)</ChartTitle>
            <div style={{ width: '100%', height: Math.max(320, combinedPercent.length * 28) }}>
              <ResponsiveContainer>
                <BarChart
                  data={combinedPercent}
                  layout="vertical"
                  margin={{ left: 16, right: 16, top: 8, bottom: 8 }}
                >
                  <XAxis
                    type="number"
                    domain={[0, Math.min(100, Math.max(5, combinedMax * 1.15))]}
                    tickFormatter={(v) => `${v}%`}
                    stroke="#A0A0A0"
                  />
                  <YAxis type="category" dataKey="label" width={110} interval={0} stroke="#A0A0A0" />
                  <Tooltip
                    formatter={(v: any, _n: any, p: any) => [`${Number(v).toFixed(1)}%`, p.payload.label]}
                    contentStyle={{ background: '#1E1E1E', border: '1px solid rgba(255,255,255,0.1)', color: '#EAEAEA' }}
                    labelStyle={{ color: '#A0A0A0' }}
                    itemStyle={{ color: '#EAEAEA' }}
                  />
                  <Bar dataKey="percent" radius={[0, 6, 6, 0]}>
                    {combinedPercent.map((entry, idx) => {
                      const fill = entry.cls === 'stock' ? palette[0] : entry.cls === 'fii' ? palette[2] : palette[1];
                      return <Cell key={`asset-cell-${idx}`} fill={fill} />;
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>

          <ChartCard>
            <ChartTitle>Percentual por ativo (alvo)</ChartTitle>
            <div style={{ width: '100%', height: Math.max(320, combinedPercentTarget.length * 28) }}>
              <ResponsiveContainer>
                <BarChart
                  data={combinedPercentTarget}
                  layout="vertical"
                  margin={{ left: 16, right: 16, top: 8, bottom: 8 }}
                >
                  <XAxis
                    type="number"
                    domain={[0, Math.min(100, Math.max(5, combinedTargetMax * 1.15))]}
                    tickFormatter={(v) => `${v}%`}
                    stroke="#A0A0A0"
                  />
                  <YAxis type="category" dataKey="label" width={110} interval={0} stroke="#A0A0A0" />
                  <Tooltip
                    formatter={(v: any, _n: any, p: any) => [`${Number(v).toFixed(1)}%`, p.payload.label]}
                    contentStyle={{ background: '#1E1E1E', border: '1px solid rgba(255,255,255,0.1)', color: '#EAEAEA' }}
                    labelStyle={{ color: '#A0A0A0' }}
                    itemStyle={{ color: '#EAEAEA' }}
                  />
                  <Bar dataKey="percent" radius={[0, 6, 6, 0]}>
                    {combinedPercentTarget.map((entry, idx) => {
                      const fill = entry.cls === 'stock' ? palette[0] : entry.cls === 'fii' ? palette[2] : palette[1];
                      return <Cell key={`asset-target-cell-${idx}`} fill={fill} />;
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>
        </ChartsGrid>
      </Content>
    </Container>
  );
};

export default PortfolioDistributionV2;
